import React, { useState, useEffect, useRef } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Input,
  message,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import ModalDelete from 'components/ModalDelete';
import getFormatedValues from 'utils/formatValues';
import Select from 'components/Select';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import {
  deleteTipoDeConceptoDeInfraccion,
  getTipoDeConceptoDeInfraccion,
  getTipoDeConceptoDeInfracciones,
  patchTipoDeConceptoDeInfraccion,
  postTipoDeConceptoDeInfraccion,
} from 'api/recaudacion/tipoDeConceptosDeInfraccion';
import { getTiposDeCargos } from 'api/recaudacion/tiposDeCargo';
import NumericInput from 'components/NumericInput';
import { getPeriodosFiscales } from 'api/catalogos/periodosFiscales';
import { getClaseDeConceptosDeInfracciones } from 'api/catalogos/claseDeConceptoDeInfracciones';
import { getYardas } from 'api/catalogos/yardas';
import { getGruas } from 'api/catalogos/gruas';
import { getExamenesMedicos } from 'api/catalogos/examenMedico';
import { getFundamentosLegales } from 'api/configuracion/fundamentosLegales';
import { getMonedas } from 'api/configuracion';

const models = ['tipodecargo', 'periodofiscal', 'yarda', 'grua'];

// eslint-disable-next-line react/prop-types
const TipoDeConceptosDeInfraccion = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [tipoDeCargoTemp, setTipoDeCargoTemp] = useState({ results: [] });
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [periodoFiscal, setPeriodoFiscal] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const repetible = Form.useWatch('es_concepto_repetible', form);
  const esCapturable = Form.useWatch('es_concepto_capturable', form);
  const claseSelected = Form.useWatch('clase_de_concepto_de_infraccion', form);

  const [claseDeConcepto, setClaseDeConcepto] = useState([]);
  const [yardaTemp, setYardaTemp] = useState({ results: [] });
  const [gruaTemp, setGruaTemp] = useState({ results: [] });
  const [examenTemp, setExamenTemp] = useState({ results: [] });
  const [fundamentos, setFundamentos] = useState([]);
  const fundamentoToString = (e) => `${e?.denominacion || ''} -  Artículo: ${e?.articulo || ''} - Fracción: ${e?.fraccion || ''} `;

  const tipoDeCargo = useRef({ results: [] });
  const examenMedico = useRef({ results: [] });
  const grua = useRef({ results: [] });
  const yarda = useRef({ results: [] });

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTipoDeConceptoDeInfracciones();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [
        _data,
        _periodosFiscales,
        _claseDeConcepto,
        _fundamentos,
        _monedas,
      ] = await Promise.all([
        getTipoDeConceptoDeInfracciones(),
        getPeriodosFiscales(),
        getClaseDeConceptosDeInfracciones(),
        getFundamentosLegales(),
        getMonedas(),
      ]);
      if (mounted) {
        setData(_data);
        setPeriodoFiscal(_periodosFiscales);
        setClaseDeConcepto(_claseDeConcepto);
        setFundamentos(_fundamentos.map((e) => ({
          ...e,
          denominacion: fundamentoToString(e),
        })));
        setMonedas(_monedas);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
    setVisibleAlert(false);
    tipoDeCargo.current = { results: [] };
    examenMedico.current = { results: [] };
    grua.current = { results: [] };
    yarda.current = { results: [] };
    setTipoDeCargoTemp({ results: [] });
    setExamenTemp({ results: [] });
    setGruaTemp({ results: [] });
    setYardaTemp({ results: [] });
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = getFormatedValues(form.getFieldsValue());
      const [key] = selectedRowKeys;
      const saved = await (key ? patchTipoDeConceptoDeInfraccion(key, values)
        : postTipoDeConceptoDeInfraccion(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTipoDeConceptoDeInfraccion(key);
      if (deleted) {
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const onClickAdd = () => {
    setVisible(true);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onClickEdit = async (id) => {
    const key = selectedRowKeys[0] || id;
    const match = await getTipoDeConceptoDeInfraccion(key);
    if (match) {
      if (match?.tipo_de_cargo) {
        const _tipoDeCargo = await getTiposDeCargos({ id: match?.tipo_de_cargo });
        setTipoDeCargoTemp(_tipoDeCargo);
      } else {
        setTipoDeCargoTemp({ results: [] });
      }
      if (match?.examen_medico) {
        const examen_medico = await getExamenesMedicos({ id: match?.examen_medico });
        setExamenTemp(examen_medico);
      } else {
        setExamenTemp({ results: [] });
      }
      if (match?.grua) {
        const _grua = await getGruas({ id: match?.grua });
        setGruaTemp(_grua);
      } else {
        setGruaTemp({ results: [] });
      }
      if (match?.yarda) {
        const _yardas = await getYardas({ id: match?.yarda });
        setYardaTemp(_yardas);
      } else {
        setYardaTemp({ results: [] });
      }
      form.setFieldsValue(match);
    }
    setVisible(true);
    setLoading(false);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
  ];

  const rules = {
    required: [
      {
        required: true,
      },
    ],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            mobileColIndex={0}
            rowKey="id"
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            hasAllViewPermission={hasAllViewPermission}
            permission={permission}
          />
        ) : (
          <Card
            bordered={false}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
              />
            )}
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Concepto `}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              initialValues={{ es_concepto_capturable: false }}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clave"
                    label="Clave"
                    hasFeedback
                    rules={rules?.required}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={16}>
                  <Form.Item
                    name="descripcion"
                    label="Descripción"
                    hasFeedback
                    rules={rules?.required}
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="descripcion_corta"
                    label="Descripción Corta"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="periodo_fiscal"
                    label="Periodo Fiscal"
                    hasFeedback
                  >
                    <Select
                      dataSource={periodoFiscal}
                      render={(e) => `${e.periodo} - ${e.identificador}${e.vigente ? ' (Vigente)' : ''}`}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clase_de_concepto_de_infraccion"
                    label="Clase de Concepto de Infracción"
                    hasFeedback
                  >
                    <Select dataSource={claseDeConcepto} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="es_concepto_capturable"
                    label="¿Es Concepto Capturable?"
                    hasFeedback
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="es_visible"
                    label="¿Es Visible?"
                    hasFeedback
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="es_concepto_repetible"
                    label="¿Es Concepto Repetible?"
                    hasFeedback
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="capturable_de_fecha_inicial"
                    label="¿Capturable de Fecha Inicial?"
                    hasFeedback
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="capturable_de_fecha_final"
                    label="¿Capturable de Fecha Final?"
                    hasFeedback
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="capturable_de_nota_de_concepto"
                    label="¿Nota de Concepto Capturable?"
                    hasFeedback
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="es_importe_capturable"
                    label="¿Es Importe Capturable?"
                    hasFeedback
                  >
                    <Select
                      trueFalse
                    />
                  </Form.Item>
                </Col>
                {repetible && (
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    label="Maximo de Concepto Repetido"
                    name="maximo_de_concepto_repetido"
                    required
                  />
                </Col>
                )}
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="tipo_de_cargo"
                    label="Tipo de Cargo"
                    hasFeedback
                    // rules={rules?.required}
                  >
                    <Select
                      dataSource={tipoDeCargoTemp.results?.length
                        ? tipoDeCargoTemp : tipoDeCargo.current}
                      usingExternalDataSource={Boolean(tipoDeCargoTemp.results?.length)}
                      labelProp="descripcion"
                      onClear={() => { form.resetFields(['tipo_de_cargo']); setTipoDeCargoTemp({ results: [] }); }}
                      pagination={{
                        getter: getTiposDeCargos,
                        setter: (dataSource) => {
                          tipoDeCargo.current = dataSource;
                        },
                        config: { auto: true, qName: 'descripcion' },
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="monedas"
                    label="Moneda"
                    hasFeedback
                  >
                    <Select dataSource={monedas} />
                  </Form.Item>
                </Col>
                {esCapturable ? (
                  <>
                    <Col xs={24} sm={24} md={8}>
                      <NumericInput
                        label="Importe Minimo"
                        name="importe_de_moneda_minima"
                        required
                        decimal
                        maxLength={32}
                      />
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <NumericInput
                        label="Importe Máximo"
                        name="importe_de_moneda_maxima"
                        required
                        decimal
                        maxLength={32}
                      />
                    </Col>
                  </>
                ) : (
                  <Col xs={24} sm={24} md={8}>
                    <NumericInput
                      label="Importe Fijo"
                      name="importe_de_moneda_fija_inicial"
                      required
                      decimal
                      maxLength={32}
                    />
                  </Col>
                )}
                {claseSelected === 2 && (
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="examen_medico"
                    label="Exámen Médico"
                    hasFeedback
                  >
                    <Select
                      dataSource={examenTemp.results?.length
                        ? examenTemp : examenMedico.current}
                      usingExternalDataSource={Boolean(examenTemp.results?.length)}
                      labelProp="descripcion"
                      onClear={() => { form.resetFields(['examen_medico']); setExamenTemp({ results: [] }); }}
                      pagination={{
                        getter: getExamenesMedicos,
                        setter: (dataSource) => {
                          examenMedico.current = dataSource;
                        },
                        config: { auto: true, qName: 'descripcion' },
                      }}
                    />
                  </Form.Item>
                </Col>
                )}
                {claseSelected === 3 && (
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="grua"
                      label="Grua"
                      hasFeedback
                    >
                      <Select
                        dataSource={gruaTemp.results?.length
                          ? gruaTemp : grua.current}
                        usingExternalDataSource={Boolean(gruaTemp.results?.length)}
                        labelProp="descripcion"
                        onClear={() => { form.resetFields(['grua']); setGruaTemp({ results: [] }); }}
                        pagination={{
                          getter: getGruas,
                          setter: (dataSource) => {
                            grua.current = dataSource;
                          },
                          config: { auto: true, qName: 'descripcion' },
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
                {claseSelected === 4 && (
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="yarda"
                    label="Yarda"
                    hasFeedback
                  >
                    <Select
                      dataSource={yardaTemp.results?.length
                        ? yardaTemp : yarda.current}
                      usingExternalDataSource={Boolean(yardaTemp.results?.length)}
                      labelProp="descripcion"
                      onClear={() => { form.resetFields(['yarda']); setYardaTemp({ results: [] }); }}
                      pagination={{
                        getter: getYardas,
                        setter: (dataSource) => {
                          yarda.current = dataSource;
                        },
                        config: { auto: true, qName: 'descripcion' },
                      }}
                    />
                  </Form.Item>
                </Col>
                )}
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="fundamento_legal"
                    label="Fundamento legal"
                    hasFeedback
                  >
                    <Select
                      dataSource={fundamentos}
                      labelProp="denominacion"
                    />
                  </Form.Item>
                </Col>

                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Tipo de Concepto ${form.getFieldValue('descripcion')}`}
        />
      </Spin>
    </Row>
  );
};
export default TipoDeConceptosDeInfraccion;
